import type { RealtimeGame, RealtimeState } from "../realtime";
import { createRng } from "../rng";

/**
 * Pull the pins: work out what order to let things go in.
 *
 * The fifth arcade game, and a deliberate reading of a genre rather than a copy
 * of it. The versions of this children have played are two-dimensional physics
 * — pins hold back liquid, you pull them, and gravity decides the rest. Physics
 * is exactly what this platform cannot have: both devices must reach an
 * identical state from an identical seed, and a solver that disagrees between
 * two phones by one pixel is a child being told their score did not add up.
 *
 * So the pins are kept and the physics is thrown away. Each pin holds a stack
 * of coloured balls. Pull one and its balls drop into a tray that fills from
 * the left, and the tray is divided into cups. What you win is the *order*:
 * which colours end up together depends entirely on which pin you pull when,
 * which is the thinking the original asks for and the only part of it that
 * matters.
 *
 * Whole numbers throughout, no clock inside the rules, and a level that is
 * solvable by construction — as everywhere else here.
 */

/** How many balls fill one cup. Four is a cup a child can count at a glance. */
export const PINDROP_CUP = 4;
export const PINDROP_FIRST_CUPS = 2;
export const PINDROP_MAX_CUPS = 5;
/** Never more pins than this, whatever the level. */
export const PINDROP_MAX_PINS = 10;

export const PINDROP_RATE = 8;
/** Two minutes, which is the length of the race rather than the difficulty. */
export const PINDROP_MAX_STEPS = 960;
/** What one cup of a single colour is worth. */
export const PINDROP_CUP_SCORE = 100;

export interface PindropState extends RealtimeState {
	kind: "pindrop";
	level: number;
	/** Each pin's balls, in the order they will drop. Empty once pulled. */
	pins: number[][];
	/** Which pins have gone, so the board can still show where they were. */
	pulled: boolean[];
	/** What has landed, in the order it landed. Cups are slices of this. */
	tray: number[];
	/** Cups won. */
	cups: number;
	level_pins: number;
}

export type PindropInput = { pull: number } | { restart: true };

/** How many cups a level has. Grows, then stops. */
export function pindropCups(level: number): number {
	return Math.min(PINDROP_FIRST_CUPS + Math.floor(level / 3), PINDROP_MAX_CUPS);
}

/** How many cups in a tray are a single colour all the way through. */
export function pindropWon(tray: readonly number[]): number {
	let won = 0;
	for (let at = 0; at + PINDROP_CUP <= tray.length; at += PINDROP_CUP) {
		const cup = tray.slice(at, at + PINDROP_CUP);
		if (cup.every((ball) => ball === cup[0])) won++;
	}
	return won;
}

/**
 * A level, built from its answer.
 *
 * The finished tray is decided first — cup one is all of one colour, cup two
 * all of another — and then cut into consecutive pieces, one per pin. Pulling
 * the pins in the order the pieces were cut rebuilds the tray exactly, so every
 * level has at least one solution and it took no searching to find.
 *
 * The pins are then shuffled, which is the whole puzzle: the pieces are in
 * front of the child in the wrong order, and the order is what they have to
 * work out.
 */
export function pindropLevel(seed: number, level: number): number[][] {
	const rng = createRng((seed + level * 2_654_435_761) >>> 0);
	const cups = pindropCups(level);

	const tray: number[] = [];
	for (let cup = 0; cup < cups; cup++) {
		for (let ball = 0; ball < PINDROP_CUP; ball++) tray.push(cup + 1);
	}

	/*
	 * Cut it into pins. More pins than cups, or the puzzle is simply "put the
	 * cups in order" — the interest is in a pin whose balls straddle two cups,
	 * which forces the ones either side of it into place.
	 */
	const pins = Math.min(cups + 1 + rng.int(cups), PINDROP_MAX_PINS);
	const cuts = new Set<number>();
	while (cuts.size < pins - 1) cuts.add(1 + rng.int(tray.length - 1));

	const pieces: number[][] = [];
	let from = 0;
	for (const cut of [...cuts].sort((a, b) => a - b)) {
		pieces.push(tray.slice(from, cut));
		from = cut;
	}
	pieces.push(tray.slice(from));

	// Shuffled, so the order they are shown in is not the order they go in.
	return rng.shuffle(pieces);
}

export const pindrop: RealtimeGame<PindropState, PindropInput> = {
	id: "pindrop",
	rate: PINDROP_RATE,
	maxSteps: PINDROP_MAX_STEPS,

	create(seed) {
		const pins = pindropLevel(seed, 0);
		return {
			kind: "pindrop",
			step: 0,
			seed,
			over: false,
			score: 0,
			level: 0,
			pins,
			pulled: pins.map(() => false),
			tray: [],
			cups: 0,
			level_pins: pins.length,
		};
	},

	parseInput(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { pull, restart } = raw as { pull?: unknown; restart?: unknown };
		if (restart === true) return { restart: true };
		if (typeof pull !== "number" || !Number.isInteger(pull)) return null;
		// An index off the wire, bounded by the most pins any level can have.
		if (pull < 0 || pull >= PINDROP_MAX_PINS) return null;
		return { pull };
	},

	step(state, input) {
		if (state.over) return state;
		const next: PindropState = { ...state, step: state.step + 1 };

		const deal = (level: number, score: number, cups: number): PindropState => {
			const pins = pindropLevel(state.seed, level);
			return {
				...next,
				level,
				pins,
				pulled: pins.map(() => false),
				tray: [],
				score,
				cups,
				level_pins: pins.length,
			};
		};

		if (input && "restart" in input) return deal(state.level, state.score, state.cups);
		if (!input) return next;

		const pin = input.pull;
		if (pin >= state.pins.length || state.pulled[pin]) return next;

		const tray = [...state.tray, ...state.pins[pin]];
		const pulled = [...state.pulled];
		pulled[pin] = true;

		if (pulled.some((gone) => !gone)) return { ...next, tray, pulled };

		// Every pin gone: score what landed, and set the next level out.
		const won = pindropWon(tray);
		return deal(state.level + 1, state.score + won * PINDROP_CUP_SCORE, state.cups + won);
	},
};

/**
 * Robo pulling pins: take whichever pin keeps the cup it is filling pure, and
 * otherwise take the smallest one.
 *
 * One pin deep, so it walks into positions a little thought would have avoided
 * — which is the point. A player that worked backwards from the answer would
 * win every level in the time and set a score no child could touch.
 */
export function pindropSteady(state: PindropState): PindropInput | null {
	const wanted = state.tray.length % PINDROP_CUP === 0 ? 0 : state.tray[state.tray.length - 1];

	let fallback: number | null = null;
	for (let pin = 0; pin < state.pins.length; pin++) {
		if (state.pulled[pin]) continue;
		const balls = state.pins[pin];
		if (wanted === 0 || balls[0] === wanted) return { pull: pin };
		if (fallback === null || balls.length < state.pins[fallback].length) fallback = pin;
	}
	return fallback === null ? null : { pull: fallback };
}
